import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { MdHowToVote, MdEdit, MdInfo, MdFavorite, MdFavoriteBorder, MdLightbulbOutline } from 'react-icons/md';
import { toast } from 'sonner';
import ShareButton from './ShareButton';
import ReportButton from './ReportButton';
import DemographicBreakdownChart from './DemographicBreakdownChart';
import PollExplanationInput from './PollExplanationInput';
import RelatedNews from './RelatedNews';
import { useRepresentative } from '../config/RepresentativeContext';

function PollCard({ poll: initialPoll, tags, onPollUpdate, showResultsByDefault = false, initialFavorited = false }) {
  const { isRepresentative, getExplanation, updateExplanation } = useRepresentative();
  const [poll, setPoll] = useState(initialPoll);
  const [showResults, setShowResults] = useState(showResultsByDefault);
  const [results, setResults] = useState(null);
  const [demographicBreakdown, setDemographicBreakdown] = useState(null);
  const [voting, setVoting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [isSwipingAway, setIsSwipingAway] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [demographics, setDemographics] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('overall');
  const [filterError, setFilterError] = useState('');
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [, setShowExplanationInput] = useState(false);
  const [isFavorited, setIsFavorited] = useState(initialFavorited);
  const [favLoading, setFavLoading] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [explanationLoading, setExplanationLoading] = useState(false);
  const [explanationError, setExplanationError] = useState('');

  const pollTags = tags.filter(tag => poll.tags.includes(tag.tagId));

  useEffect(() => {
    setPoll(initialPoll);
  }, [initialPoll]);

  useEffect(() => {
    setIsFavorited(initialFavorited);
  }, [initialFavorited]);

  const toggleFavorite = async () => {
    if (favLoading) return;
    setFavLoading(true);
    try {
      if (isFavorited) {
        await axios.delete(`${API_URL}/api/favorites`, { data: { item_type: 'poll', item_id: String(poll.pollId) } });
        setIsFavorited(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API_URL}/api/favorites`, { item_type: 'poll', item_id: String(poll.pollId) });
        setIsFavorited(true);
        toast.success('Added to favorites');
      }
    } catch (err) {
      toast.error('Failed to update favorite');
    } finally {
      setFavLoading(false);
    }
  };

  const handleExplain = async () => {
    if (explanation) {
      setShowExplanation(!showExplanation);
      return;
    }
    setShowExplanation(true);
    setExplanationLoading(true);
    setExplanationError('');
    try {
      const res = await axios.get(`${API_URL}/api/polls/${poll.pollId}/explain`);
      setExplanation(res.data.explanation);
    } catch (err) {
      setExplanationError(err.response?.data?.detail || 'Failed to get explanation. Please try again.');
    } finally {
      setExplanationLoading(false);
    }
  };

  useEffect(() => {
    if (showResultsByDefault && poll.userAnswered) {
      loadResults();
      loadDemographics();
      setShowResults(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResultsByDefault, poll.userAnswered]);

  const handleVote = async (answer) => {
    const isEditingExisting = poll.userAnswered && editing;
    const previousAnswer = poll.userAnswer;
    const previousResults = results;
    
    // Optimistic UI update - immediately show the new answer
    if (isEditingExisting) {
      // Update local state immediately for responsive UI
      setPoll(prev => ({
        ...prev,
        userAnswer: answer
      }));
      
      // Optimistically update results if we have them
      if (results) {
        const oldAnswer = previousAnswer?.toLowerCase();
        const newAnswer = answer.toLowerCase();
        
        if (oldAnswer !== newAnswer) {
          setResults(prev => {
            if (!prev) return prev;
            const newYes = prev.yes + (newAnswer === 'yes' ? 1 : 0) - (oldAnswer === 'yes' ? 1 : 0);
            const newNo = prev.no + (newAnswer === 'no' ? 1 : 0) - (oldAnswer === 'no' ? 1 : 0);
            const total = newYes + newNo;
            return {
              ...prev,
              yes: newYes,
              no: newNo,
              yesPercentage: total > 0 ? Math.round((newYes / total) * 100) : 0,
              noPercentage: total > 0 ? Math.round((newNo / total) * 100) : 0
            };
          });
        }
      }
      
      setEditing(false);
      setVoting(false);
    } else {
      setVoting(true);
    }
    
    try {
      if (isEditingExisting) {
        // Fire and forget for edits - UI already updated
        axios.put(`${API_URL}/api/polls/${poll.pollId}/respond`, { answer })
          .catch(error => {
            // Revert on error
            console.error('Failed to update vote:', error);
            setPoll(prev => ({ ...prev, userAnswer: previousAnswer }));
            setResults(previousResults);
            alert(error.response?.data?.detail || 'Failed to update vote. Please try again.');
          });
        
        // Notify parent (don't remove from list for edits)
        if (onPollUpdate) {
          onPollUpdate(poll.pollId, true);
        }
      } else {
        // For new votes, wait for response before animating
        await axios.post(`${API_URL}/api/polls/${poll.pollId}/respond`, { answer });
        setVoting(false);
        
        // For representatives, don't swipe away - show explanation input instead
        if (isRepresentative) {
          setPoll(prev => ({
            ...prev,
            userAnswered: true,
            userAnswer: answer
          }));
          setShowExplanationInput(true);
          loadResults();
          loadDemographics();
          setShowResults(true);
          
          if (onPollUpdate) {
            onPollUpdate(poll.pollId, true); // Keep in list
          }
        } else {
          // Regular users - swipe away
          setIsSwipingAway(true);
          
          setTimeout(() => {
            setIsHidden(true);
            if (onPollUpdate) {
              onPollUpdate(poll.pollId);
            }
          }, 500);
        }
      }
      
    } catch (error) {
      // Only reaches here for new votes (edits handle errors in .catch above)
      alert(error.response?.data?.detail || 'Failed to submit vote');
      setVoting(false);
    }
  };

  const loadDemographics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/polls/${poll.pollId}/demographics`);
      setDemographics(response.data.demographics);
    } catch (error) {
      console.error('Failed to load demographics:', error);
    }
  };

  const loadResults = async () => {
    try {
      setFilterError('');
      
      if (!results) {
        setLoadingResults(true);
        const cumulativeUrl = `${API_URL}/api/polls/${poll.pollId}/results`;
        const cumulativeResponse = await axios.get(cumulativeUrl);
        setResults(cumulativeResponse.data);
        setLoadingResults(false);
      }
      
      setShowResults(true);
    } catch (error) {
      setLoadingResults(false);
      if (error.response?.status === 400) {
        setFilterError(error.response.data.detail);
      } else {
        console.error('Failed to load results:', error);
      }
    }
  };

  const loadDemographicBreakdown = async (demographic) => {
    try {
      setFilterError('');
      setLoadingBreakdown(true);
      
      const response = await axios.get(`${API_URL}/api/polls/${poll.pollId}/demographic-breakdown?demographic=${demographic}`);
      setDemographicBreakdown(response.data);
      setLoadingBreakdown(false);
    } catch (error) {
      setLoadingBreakdown(false);
      if (error.response?.status === 400) {
        setFilterError(error.response.data.detail);
      } else {
        console.error('Failed to load demographic breakdown:', error);
      }
    }
  };

  const handleFilterChange = async (filterType) => {
    setSelectedFilter(filterType);
    setFilterError('');
    
    if (filterType === 'overall') {
      setDemographicBreakdown(null);
    } else {
      await loadDemographicBreakdown(filterType);
    }
  };

  const cumulativeBarChartData = results ? [
    { name: 'Yes', value: results.results.yes.count, color: '#08B23C', percentage: results.results.yes.percentage },
    { name: 'No', value: results.results.no.count, color: '#D33D3D', percentage: results.results.no.percentage }
  ] : [];

  if (isHidden) {
    return null;
  }

  return (
    <div 
      className={`poll-card ${isSwipingAway ? 'swipe-away' : ''}`} 
      data-testid={`poll-card-${poll.pollId}`}
    >
      <div className="mb-5">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-800 sm:flex-1 font-rubik" data-testid="poll-question">{poll.question}</h3>
          <div className="flex items-center gap-3 sm:ml-4 flex-shrink-0">
            <button
              onClick={handleExplain}
              disabled={explanationLoading}
              className={`transition-colors ${showExplanation && explanation ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'} ${explanationLoading ? 'animate-pulse' : ''}`}
              data-testid={`explain-btn-${poll.pollId}`}
              title="Explain this question"
            >
              <MdLightbulbOutline className="text-xl" />
            </button>
            <button
              onClick={toggleFavorite}
              disabled={favLoading}
              className={`transition-colors ${isFavorited ? 'text-red-500' : 'text-gray-300 hover:text-red-400'}`}
              data-testid={`fav-btn-${poll.pollId}`}
              title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              {isFavorited ? <MdFavorite className="text-xl" /> : <MdFavoriteBorder className="text-xl" />}
            </button>
            <ReportButton pollId={poll.pollId} />
            <ShareButton pollId={poll.pollId} pollQuestion={poll.question} />
            {poll.userAnswered && !editing && (
              <button
                onClick={() => setEditing(true)}
                className="text-tertiary hover:text-primary text-sm font-medium flex items-center gap-1 font-rubik"
                data-testid="edit-response-link"
              >
                <MdEdit className="text-base" />
                Edit
              </button>
            )}
          </div>
        </div>
        {pollTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {pollTags.map(tag => (
              <span
                key={tag.tagId}
                className="tag-pill text-white font-rubik"
                style={{ backgroundColor: tag.color }}
                data-testid={`poll-tag-${tag.name}`}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* AI Explanation Section */}
      {showExplanation && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 overflow-hidden" data-testid={`explanation-section-${poll.pollId}`}>
          {explanationLoading ? (
            <div className="flex items-center gap-3 px-5 py-4" data-testid={`explanation-loading-${poll.pollId}`}>
              <div className="relative w-5 h-5 flex-shrink-0">
                <div className="absolute inset-0 rounded-full border-2 border-amber-300 border-t-amber-600 animate-spin"></div>
              </div>
              <p className="text-sm text-amber-800 font-rubik font-medium">Asking AI to explain this...</p>
            </div>
          ) : explanationError ? (
            <div className="px-5 py-4" data-testid={`explanation-error-${poll.pollId}`}>
              <p className="text-sm text-red-600 font-rubik">{explanationError}</p>
              <button onClick={handleExplain} className="text-xs text-amber-700 hover:text-amber-900 font-rubik font-medium mt-1 underline">Try again</button>
            </div>
          ) : explanation ? (
            <div className="px-5 py-4 space-y-3" data-testid={`explanation-content-${poll.pollId}`}>
              <div className="flex items-start gap-2">
                <MdLightbulbOutline className="text-amber-600 text-lg flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700 font-rubik leading-relaxed">{explanation.explanation}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3" data-testid={`explanation-pro-${poll.pollId}`}>
                  <p className="text-xs font-semibold text-green-700 font-rubik mb-1 uppercase tracking-wide">For "Yes"</p>
                  <p className="text-sm text-green-900 font-rubik leading-relaxed">{explanation.pro_argument}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid={`explanation-con-${poll.pollId}`}>
                  <p className="text-xs font-semibold text-red-700 font-rubik mb-1 uppercase tracking-wide">For "No"</p>
                  <p className="text-sm text-red-900 font-rubik leading-relaxed">{explanation.con_argument}</p>
                </div>
              </div>
              <button
                onClick={() => setShowExplanation(false)}
                className="text-xs text-gray-500 hover:text-gray-700 font-rubik"
                data-testid={`collapse-explanation-${poll.pollId}`}
              >
                Hide explanation
              </button>
              <div className="pt-3 border-t border-amber-200">
                <RelatedNews
                  endpoint={`/api/news/poll/${poll.pollId}`}
                  limit={3}
                  title="Related News"
                  testIdPrefix={`poll-news-${poll.pollId}`}
                  compact
                />
              </div>
            </div>
          ) : null}
        </div>
      )}

      {!poll.userAnswered || editing ? (
        <div className="space-y-3">
          <div className="flex gap-3">
            <button
              onClick={() => handleVote('yes')}
              disabled={voting}
              className="flex-1 btn-primary disabled:opacity-50"
              data-testid="vote-yes-button"
            >
              Yes
            </button>
            <button
              onClick={() => handleVote('no')}
              disabled={voting}
              className="flex-1 btn-danger disabled:opacity-50"
              data-testid="vote-no-button"
            >
              No
            </button>
          </div>
          {editing && (
            <button
              onClick={() => setEditing(false)}
              className="w-full text-gray-600 py-2 text-sm hover:text-gray-800 font-rubik"
              data-testid="cancel-edit-button"
            >
              Cancel
            </button>
          )}
        </div>
      ) : (
        <div className="bg-green-50 border border-primary rounded-card p-4" data-testid="user-answer-display">
          <p className="text-primary font-medium font-rubik flex items-center gap-2">
            <MdHowToVote className="text-xl" />
            Your answer: <span className="font-bold capitalize">{poll.userAnswer}</span>
          </p>
        </div>
      )}

      {/* Loading Results Indicator */}
      {loadingResults && (
        <div className="mt-6 pt-6 border-t" data-testid="loading-results">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative w-48 h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
              <div className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-primary rounded-full animate-loading-bar"></div>
            </div>
            <p className="text-gray-600 font-rubik font-medium flex items-center gap-2">
              <svg className="animate-spin h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading Poll Results...
            </p>
          </div>
        </div>
      )}

      {showResults && results && (
        <div className="mt-6 pt-6 border-t" data-testid="poll-results">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-semibold text-gray-800 font-rubik flex items-center gap-2">
              <MdHowToVote className="text-xl text-primary" />
              Results Analytics
            </h4>
            <ShareButton pollId={poll.pollId} pollQuestion={poll.question} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* LEFT SIDE: Cumulative Results */}
            <div className="bg-gray-50 rounded-card p-6 border border-gray-200">
              <h5 className="text-base font-semibold text-gray-800 font-rubik mb-4">Cumulative Results</h5>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 font-rubik mb-2">
                  Total Votes: <span className="font-semibold text-gray-800">{results.totalResponses}</span>
                </p>
              </div>

              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={cumulativeBarChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontFamily: 'Rubik', fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontFamily: 'Rubik', fontSize: 14 }} width={50} />
                  <RechartsTooltip 
                    contentStyle={{ fontFamily: 'Rubik' }}
                    formatter={(value, name, props) => [
                      `${value} votes (${props.payload.percentage}%)`,
                      name
                    ]}
                  />
                  <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                    {cumulativeBarChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-primary"></div>
                    <span className="text-sm font-medium text-gray-700 font-rubik">Yes</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-800 font-rubik">{results.results.yes.percentage}% ({results.results.yes.count})</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-danger"></div>
                    <span className="text-sm font-medium text-gray-700 font-rubik">No</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-800 font-rubik">{results.results.no.percentage}% ({results.results.no.count})</span>
                </div>
              </div>
            </div>

            {/* RIGHT SIDE: Filtered Results with Demographic Filters */}
            <div className="bg-blue-50 rounded-card p-6 border border-blue-200">
              <h5 className="text-base font-semibold text-gray-800 font-rubik mb-4">Demographic Filtering</h5>

              {demographics && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button
                      onClick={() => handleFilterChange('overall')}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'overall'
                          ? 'bg-primary text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      Clear Filter
                    </button>
                    <button
                      onClick={() => handleFilterChange('country')}
                      disabled={!demographics.country.available}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'country'
                          ? 'bg-secondary text-white'
                          : demographics.country.available
                          ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300'
                      }`}
                      title={!demographics.country.available ? 'Update your profile with country info' : ''}
                    >
                      Country
                    </button>
                    <button
                      onClick={() => handleFilterChange('state')}
                      disabled={!demographics.state.available}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'state'
                          ? 'bg-secondary text-white'
                          : demographics.state.available
                          ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300'
                      }`}
                      title={!demographics.state.available ? 'Update your profile with state info' : ''}
                    >
                      State
                    </button>
                    <button
                      onClick={() => handleFilterChange('gender')}
                      disabled={!demographics.gender.available}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'gender'
                          ? 'bg-secondary text-white'
                          : demographics.gender.available
                          ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300'
                      }`}
                      title={!demographics.gender.available ? 'Update your profile with gender info' : ''}
                    >
                      Gender
                    </button>
                    <button
                      onClick={() => handleFilterChange('generation')}
                      disabled={!demographics.generation.available}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'generation'
                          ? 'bg-secondary text-white'
                          : demographics.generation.available
                          ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300'
                      }`}
                      title={!demographics.generation.available ? 'Update your profile with birthdate info' : ''}
                    >
                      Generation
                    </button>
                    <button
                      onClick={() => handleFilterChange('military_service')}
                      disabled={!demographics.military_service?.available}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'military_service'
                          ? 'bg-secondary text-white'
                          : demographics.military_service?.available
                          ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300'
                      }`}
                      title={!demographics.military_service?.available ? 'Update your profile with military service status' : ''}
                    >
                      Military
                    </button>
                  </div>

                  {filterError && (
                    <div className="bg-red-50 border border-danger rounded-card p-3 mb-4">
                      <p className="text-danger font-rubik text-xs flex items-center gap-2">
                        <MdInfo className="text-sm" />
                        {filterError}
                      </p>
                      <Link to="/profile" className="text-xs text-tertiary hover:text-primary font-medium mt-1 inline-block">
                        → Update Profile
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Loading indicator for demographic breakdown */}
              {loadingBreakdown && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="relative w-32 h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
                    <div className="absolute inset-0 bg-gradient-to-r from-secondary via-primary to-secondary rounded-full animate-loading-bar"></div>
                  </div>
                  <p className="text-sm text-gray-600 font-rubik">Loading breakdown...</p>
                </div>
              )}

              {/* Demographic Breakdown Chart */}
              {!loadingBreakdown && demographicBreakdown && selectedFilter !== 'overall' ? (
                <DemographicBreakdownChart 
                  data={demographicBreakdown.breakdown} 
                  demographic={selectedFilter}
                />
              ) : !loadingBreakdown && selectedFilter === 'overall' ? (
                <div className="text-center py-8">
                  <MdInfo className="text-4xl text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 font-rubik">
                    Select a demographic filter to view the breakdown
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Representative Explanation Input */}
      {isRepresentative && poll.userAnswered && (
        <PollExplanationInput
          pollId={poll.pollId}
          existingExplanation={getExplanation(poll.pollId)}
          onSave={(explanation) => updateExplanation(poll.pollId, explanation)}
        />
      )}
    </div>
  );
}

export default PollCard;
